<template>
    <ModalWrapper v-model="isShowModal">
        <template v-slot:activator>
            <v-btn color="primary" data-cy="copy-contract" class="" @click="isShowModal = true">
                {{ $t('copy_contract') }}
            </v-btn>
        </template>
        <template v-slot:title>
            {{ $t('copy_contract') }}
        </template>
        <template v-slot:content>
            <v-form ref="form" v-model="valid">
                <v-select
                    v-model="contractListType"
                    :items="useContractListType"
                    data-cy="type-of-contract"
                    @update:model-value="resetData"
                />
                <v-autocomplete
                    v-if="contractListType === 'GROUP'"
                    v-model="contractId"
                    :items="contractGroups"
                    :label="`${$t('name')}*`"
                    item-value="id"
                    item-title="name"
                    clearable
                    data-cy="contract-group"
                    hide-details="auto"
                    class="mb-4"
                    :rules="[requiredRule(`contractTypes.${contractListType}`)]"
                />
                <company-autocomplete
                    v-if="contractListType === 'CLIENTS'"
                    v-model="contractId"
                    class="mb-4"
                    :label="`${$t('choose.company_long')}*`"
                    :types="['TOUR_OPERATOR', 'TOUR_AGENCY', 'CORPORATOR']"
                    phone-search
                    outlined
                    data-cy="client-contract"
                />
                <div class="text-subtitle-1 font-weight-medium">{{ $t('copy_contract_note') }}</div>
                <v-row no-gutters class="gc-5 pb-2">
                    <v-btn variant="text" color="primary" @click="selectAll">
                        {{ $t('select_all') }}
                    </v-btn>
                    <v-btn variant="text" color="primary" @click="deselectAll">
                        {{ $t('deselect_all') }}
                    </v-btn>
                </v-row>
                <v-card variant="outlined">
                    <v-card-text>
                        <v-row no-gutters>
                            <v-col v-for="(tab, key) in selectedTabs" :key="key" cols="12" sm="6">
                                <v-checkbox
                                    v-model="selectedTabs[key]"
                                    hide-details
                                    :label="$t(`sales_settings_tabs.${key}`)"
                                    :data-cy="key"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-form>
            <div v-if="isShowError" class="text-error">{{ $t('contract_copying_error') }}</div>
        </template>
        <template v-slot:btns>
            <v-btn
                :disabled="isLoading || isShowError || !valid"
                color="primary"
                variant="flat"
                data-cy="copy"
                @click="copyContract"
            >
                {{ $t('copy') }}
            </v-btn>
        </template>
    </ModalWrapper>
</template>
<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {
        contractGroupGetToPutTransformer,
        copyContractGroupEntries,
        getContractGroupFieldsMapper,
    } from '~/adapters/salesSettingsAdapter'
    import CompanyAutocomplete from '~/components/account/modals/newOrderRequest/CompanyAutocomplete.vue'

    const {contractGroups} = useContractGroups()

    const props = defineProps<{
        availableTabs: string[]
        availableClientsContracts?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'copied'): void
    }>()

    const formData = defineModel<any>()
    const isShowModal = ref<boolean>(false)
    const selectedTabs = ref({})
    const contractId = ref(null)
    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const selectedContract = ref({})
    const contractListType = ref('GROUP')
    const isTAContract: boolean = inject('isTAContract', false)
    const isCorpContract: boolean = inject('isCorpContract', false)
    const isShowCalculationCommission = inject('isShowCalculationCommission', false)
    const contractGroupPlace = inject('contractGroupPlace')
    const contractGroupFieldsMapper = ref({})

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    watch(isShowModal, async () => {
        if (!isShowModal.value) {
            initTabSelection()
            contractId.value = null
        }
    })

    onMounted(async () => {
        initTabSelection()
        contractGroupFieldsMapper.value = getContractGroupFieldsMapper(
            isShowCalculationCommission,
            isTAContract,
            isCorpContract
        )
    })

    const initTabSelection = () => {
        selectedTabs.value = props.availableTabs.reduce((acc, curr) => {
            acc[curr] = true
            return acc
        }, {})
    }

    const selectAll = () => {
        Object.keys(selectedTabs.value).forEach(key => (selectedTabs.value[key] = true))
    }

    const deselectAll = () => {
        Object.keys(selectedTabs.value).forEach(key => (selectedTabs.value[key] = false))
    }

    const resetData = () => {
        contractId.value = null
        selectedContract.value = {}
    }

    const getGroupOfContracts = async () => {
        isLoading.value = true
        try {
            const resp = await $api.contractGroup.get({
                contractGroupId: contractId.value,
                contractGroupPlace: contractGroupPlace,
            })
            selectedContract.value = contractGroupGetToPutTransformer(resp)
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const getCompanyContract = async () => {
        isLoading.value = true
        try {
            const resp = await $api.companyContract.get({companyId: contractId.value.id})
            selectedContract.value = contractGroupGetToPutTransformer(resp.contractGroup)
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const isShowError = computed(() => {
        return !Object.values(selectedTabs.value).some(value => value === true)
    })

    const copyContract = async () => {
        if (contractListType.value === 'GROUP') await getGroupOfContracts()
        else await getCompanyContract()
        const trueKeys = Object.keys(selectedTabs.value).filter(key => selectedTabs.value[key])
        const updatedData = await copyContractGroupEntries(
            contractGroupFieldsMapper.value,
            trueKeys,
            selectedContract.value
        )
        formData.value = {...formData.value, ...updatedData}

        emit('copied')
        EventBus.$emit('UPDATE_CONTRACT')
        isShowModal.value = false
    }

    const useContractListType = computed(() => {
        const contractTypes = ['GROUP']
        if (props.availableClientsContracts) contractTypes.unshift('CLIENTS')
        return contractTypes.map(value => ({value, title: t(`contractTypes.${value}`)}))
    })
</script>
